/* ============================================================
 * BONJIL · Giveaway entry form → Klaviyo
 * Used by sections/bonjil__giveaway-hero.liquid (native form mode).
 *
 * Flow on submit
 *   1. Client-side validation (first name, email, rules consent, phone format)
 *      + bot checks (honeypot field, minimum time on page).
 *   2. Klaviyo Client Events API: metric `cfg.metric` ("Entered Giveaway")
 *      on the entrant's profile, with consent flags, campaign key and UTMs.
 *      This is the record of entry — it does NOT imply marketing consent.
 *   3. Klaviyo Client Subscriptions API — ONLY for channels the entrant
 *      ticked (email opt-in / SMS opt-in), same pattern as assets/quiz.js.
 *   4. Meta Pixel "Lead" (optional) → redirect to the thank-you page.
 *
 * Config (set by the section): window.BONJIL_GIVEAWAY = {
 *   formId, publicKey, listId, metric, campaign, thankYouUrl, fireMetaLead, sourcePage
 * }
 * ============================================================ */
(function () {
  "use strict";

  var cfg = window.BONJIL_GIVEAWAY || {};
  var form = document.getElementById(cfg.formId || "");
  if (!form) return;

  var REVISION = "2026-04-15";
  var MIN_SECONDS_ON_PAGE = 3;
  var renderedAt = Date.now();
  var submitting = false;

  var phoneInput = form.querySelector("[data-gw-phone]");
  var smsBox = form.querySelector("[data-gw-sms]");
  var submitBtn = form.querySelector("[data-gw-submit]");
  var submitLabel = submitBtn ? submitBtn.textContent : "";

  // ---- SMS disclosure appears only once a phone number is typed ----
  if (phoneInput && smsBox) {
    var toggleSms = function () { smsBox.hidden = phoneInput.value.trim() === ""; };
    phoneInput.addEventListener("input", toggleSms);
    toggleSms();
  }

  // ---- helpers ----
  function showError(name, on) {
    var el = form.querySelector('[data-error-for="' + name + '"]');
    var input = form.querySelector('[name="' + name + '"]');
    if (el) el.classList.toggle("is-visible", !!on);
    if (input && input.type !== "checkbox") input.setAttribute("aria-invalid", on ? "true" : "false");
  }
  function clearErrors() {
    form.querySelectorAll(".gw-form__error.is-visible").forEach(function (el) { el.classList.remove("is-visible"); });
    form.querySelectorAll('[aria-invalid="true"]').forEach(function (el) { el.setAttribute("aria-invalid", "false"); });
  }
  // E.164 for US numbers (same rules as the quiz); "" when it can't be formatted.
  function formatPhone(raw) {
    if (!raw) return "";
    var digits = raw.replace(/\D/g, "");
    if (raw.trim().charAt(0) === "+" && digits.length >= 10) return "+" + digits;
    if (digits.length === 10) return "+1" + digits;
    if (digits.length === 11 && digits.charAt(0) === "1") return "+" + digits;
    return "";
  }
  function utmProps(prefix) {
    var params = new URLSearchParams(window.location.search);
    var map = { utm_source: "UTM Source", utm_medium: "UTM Medium", utm_campaign: "UTM Campaign", utm_content: "UTM Content", utm_term: "UTM Term" };
    var out = {};
    Object.keys(map).forEach(function (k) { var v = params.get(k); if (v) out[prefix + map[k]] = v; });
    return out;
  }
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0; return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
  function klaviyoPost(path, body) {
    return fetch("https://a.klaviyo.com/client/" + path + "/?company_id=" + encodeURIComponent(cfg.publicKey), {
      method: "POST",
      headers: { "Content-Type": "application/json", revision: REVISION },
      body: JSON.stringify(body),
      keepalive: true
    });
  }
  function withTimeout(promise, ms) {
    return Promise.race([promise, new Promise(function (resolve) { setTimeout(function () { resolve(null); }, ms); })]);
  }

  // ---- Klaviyo: entry event (creates/updates the profile too) ----
  function trackEntry(entry) {
    var profileAttrs = {
      email: entry.email,
      first_name: entry.firstName,
      properties: Object.assign({
        "Giveaway Campaign": cfg.campaign,
        "Giveaway Entered At": entry.at,
        "Giveaway Rules Consent": true,
        "Giveaway Email Opt-In": entry.emailConsent,
        "Giveaway SMS Opt-In": entry.smsConsent,
        "Giveaway Source Page": cfg.sourcePage
      }, utmProps("Giveaway "))
    };
    if (entry.phone) profileAttrs.phone_number = entry.phone;

    var body = {
      data: {
        type: "event",
        attributes: {
          unique_id: entry.id,
          time: entry.at,
          properties: Object.assign({
            "Campaign": cfg.campaign,
            "Entry ID": entry.id,
            "Rules Consent": true,
            "Email Opt-In": entry.emailConsent,
            "SMS Opt-In": entry.smsConsent,
            "Phone Provided": !!entry.phone,
            "Source Page": cfg.sourcePage,
            "Entry URL": window.location.href.split("#")[0]
          }, utmProps("")),
          metric: { data: { type: "metric", attributes: { name: cfg.metric || "Entered Giveaway" } } },
          profile: { data: { type: "profile", attributes: profileAttrs } }
        }
      }
    };
    return klaviyoPost("events", body);
  }

  // ---- Klaviyo: marketing subscriptions, only for ticked channels ----
  function subscribe(entry) {
    if (!cfg.listId) return Promise.resolve(null);
    var subscriptions = {};
    if (entry.emailConsent) subscriptions.email = { marketing: { consent: "SUBSCRIBED" } };
    if (entry.phone && entry.smsConsent) subscriptions.sms = { marketing: { consent: "SUBSCRIBED" } };
    if (!subscriptions.email && !subscriptions.sms) return Promise.resolve(null);

    var profileAttrs = { email: entry.email, first_name: entry.firstName, subscriptions: subscriptions };
    if (entry.phone) profileAttrs.phone_number = entry.phone;

    return klaviyoPost("subscriptions", {
      data: {
        type: "subscription",
        attributes: {
          custom_source: "Giveaway — " + (cfg.campaign || "entry form"),
          profile: { data: { type: "profile", attributes: profileAttrs } }
        },
        relationships: { list: { data: { type: "list", id: cfg.listId } } }
      }
    }).catch(function (e) { console.warn("[giveaway] subscribe failed:", e); return null; });
  }

  function setBusy(on) {
    submitting = on;
    if (!submitBtn) return;
    submitBtn.disabled = on;
    submitBtn.textContent = on ? (submitBtn.getAttribute("data-busy-label") || submitLabel) : submitLabel;
  }

  // ---- submit ----
  form.addEventListener("submit", function (e) {
    e.preventDefault();
    if (submitting) return;
    clearErrors();

    var first = form.elements.first_name, email = form.elements.email, phone = form.elements.phone;
    var rules = form.elements.rules_consent, emailOpt = form.elements.email_consent, smsOpt = form.elements.sms_consent;

    var ok = true;
    if (!first.value.trim()) { showError("first_name", true); ok = false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) { showError("email", true); ok = false; }
    var formattedPhone = "";
    if (phone && phone.value.trim()) {
      formattedPhone = formatPhone(phone.value);
      if (!formattedPhone) { showError("phone", true); ok = false; }
    }
    if (!rules.checked) { showError("rules_consent", true); ok = false; }
    if (!ok) {
      var firstBad = form.querySelector('[aria-invalid="true"]') || rules;
      if (firstBad && firstBad.focus) firstBad.focus();
      return;
    }

    // Bot checks: honeypot filled, or submitted faster than a human could.
    var hp = form.elements.gw_hp; // nonsense name so browser autofill never fills it
    if ((hp && hp.value) || (Date.now() - renderedAt) < MIN_SECONDS_ON_PAGE * 1000) {
      showError("form", true);
      return;
    }
    if (!cfg.publicKey) { console.warn("[giveaway] Klaviyo public key missing"); showError("form", true); return; }

    var entry = {
      id: uuid(),
      at: new Date().toISOString(),
      firstName: first.value.trim(),
      email: email.value.trim().toLowerCase(),
      phone: formattedPhone,
      emailConsent: !!(emailOpt && emailOpt.checked),
      smsConsent: !!(formattedPhone && smsOpt && smsOpt.checked)
    };

    setBusy(true);
    trackEntry(entry).then(function (res) {
      if (!res || !res.ok) {
        console.warn("[giveaway] Klaviyo event returned " + (res && res.status));
        showError("form", true);
        setBusy(false);
        return;
      }
      // Entry is recorded. Subscriptions are best-effort (short wait), then go.
      return withTimeout(subscribe(entry), 1500).then(function () {
        try { if (cfg.fireMetaLead && typeof fbq === "function") fbq("track", "Lead"); } catch (err) {}
        try { sessionStorage.setItem("bonjil.giveaway.entered", entry.at); } catch (err) {}
        if (cfg.thankYouUrl) window.location.assign(cfg.thankYouUrl);
        else setBusy(false);
      });
    }).catch(function (err) {
      console.warn("[giveaway] Klaviyo event failed:", err);
      showError("form", true);
      setBusy(false);
    });
  });

  form.querySelectorAll("input").forEach(function (i) {
    i.addEventListener("input", function () { showError(i.name, false); showError("form", false); });
    i.addEventListener("change", function () { showError(i.name, false); });
  });
})();
